import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import {
    LogIn,
    LogOut as LogOutIcon,
    ScanLine,
    Camera,
    X,
    Car,
    Bike,
    User,
    UserPlus,
    Phone,
    Sparkles,
    QrCode,
    CheckCircle2,
    RefreshCw,
    Clock,
    Filter,
    Type,
    Mic,
    MicOff,
    Users,
    ShieldCheck,
    Languages
} from "lucide-react";
import ShareWhatsApp from "@/components/ShareWhatsApp";
import VehicleGateLookup from "@/components/VehicleGateLookup";
import VisitorPhotoModal, { getVisitorPhotoSrc } from "@/components/VisitorPhotoModal";
import QRScannerModal from "@/components/QRScannerModal";
import DailyStaffGateModal from "@/components/DailyStaffGateModal";

export const I18N = {
    en: {
        consoleTitle: "Security Gate Console",
        activeDuty: "Active Duty Guard Desk · Large touch-friendly controls",
        seniorMode: "Senior Mode",
        largeTextOn: "Large Text Active",
        largeTextOff: "Standard View",
        langToggle: "हिन्दी",
        // 4 Primary Action Cards
        cardWalkin: "Walk-in Visitor",
        cardWalkinSub: "New guest or delivery entry",
        cardScanQR: "Scan QR Pass",
        cardScanQRSub: "Scan visitor or staff pass",
        cardVehicle: "Vehicle Check",
        cardVehicleSub: "Search plate & resident flat",
        cardStaff: "Daily Staff",
        cardStaffSub: "Attendance & inside status",
        // Scanner & Code Verification
        passCodeTitle: "PASS CODE VERIFICATION",
        manualVerification: "Enter Code / Phone",
        codePlaceholder: "Enter 8-digit code (e.g. A3F9B2C7 or DH-XXXX)",
        codeTip: "💡 Tip: Verify 8-digit visitor pass, walk-in code, staff code (DH-XXXX), or mobile number.",
        verifyBtn: "Verify",
        // Verified Pass Card
        verifiedPass: "Verified Pass",
        walkinGuest: "Walk-in Guest",
        dailyStaff: "Daily Staff",
        clearNext: "Clear / Next",
        call: "Call",
        addPhoto: "Add Photo",
        uploading: "Uploading…",
        markEntry: "MARK ENTRY",
        markExit: "MARK EXIT",
        status: "Status",
        passId: "Pass ID",
        // Recent Activity
        todayAtGate: "Today at the gate",
        recentActivity: "Recent activity",
        refresh: "Refresh",
        syncing: "Syncing…",
        all: "All",
        inside: "Inside",
        visitors: "Visitors",
        staff: "Staff",
        noActivity: "No activity recorded for this view.",
        noActivitySub: "Passes verified, walk-ins, and staff check-ins will show up here.",
        entryBtn: "Entry",
        exitBtn: "Exit",
        // Status Labels
        stEntered: "Inside",
        stExited: "Exited",
        stApproved: "Approved",
        stPending: "Pending",
        stRejected: "Rejected",
        // Walkin Modal
        gateEntryDesk: "Gate Entry Desk",
        newWalkin: "New Walk-in Visitor",
        visitorPhotoReq: "Visitor Photo (Required)",
        openCamera: "Tap to Open Camera / Take Photo",
        clickToTake: "Click here to take visitor picture with camera",
        changePhoto: "Tap to change photo",
        visitorFullName: "Visitor Full Name *",
        spokenAs: "Spoken as First & Last Name",
        refreshMic: "Refresh Mic",
        namePlaceholder: "Enter or speak full name (e.g. Ramesh Sharma)",
        listeningName: "Listening... Speak full name now",
        towerWing: "Tower / Wing *",
        selectTower: "Select Tower",
        flatNumber: "Flat Number *",
        flatPlaceholder: "e.g. 402",
        purposeOfVisit: "Purpose of Visit",
        visitorVehicle: "Visitor Vehicle (Car / Bike / On Foot)",
        onFoot: "On Foot",
        car: "Car",
        bike: "Bike",
        plateNumber: "Vehicle License Plate Number",
        platePlaceholderBike: "e.g. MH 12 CD 5678",
        platePlaceholderCar: "e.g. MH 12 AB 1234",
        visitorPhone: "Visitor Mobile Number (Optional)",
        phonePlaceholder: "Speak or enter 10 digits (e.g. 9876543210)",
        listeningPhone: "Listening... Speak 10 digits",
        digitsCount: "digits",
        micHelpTitle: "Mic Help:",
        micHelpBody: "Tap 🎤 to speak. If speech recognition is stuck, tap Refresh Mic to unlock it.",
        sendForApproval: "Send to Resident for Approval",
        sendingApproval: "Uploading & Sending…",
        // Walkin Result
        walkinSent: "Walk-in Sent",
        destFlat: "Destination Flat",
        resident: "Resident",
        currentStatus: "Current Status:",
        residentNotified: "Resident has been notified via Push & Notification. You can also share the approval link via WhatsApp:",
        doneClose: "Done / Close",
        sendToResidentWA: "Send to resident",
    },
    hi: {
        consoleTitle: "सुरक्षा गेट कंसोल",
        activeDuty: "ड्यूटी पर गार्ड डेस्क · बड़े टच बटन व आसान उपयोग",
        seniorMode: "सीनियर मोड",
        largeTextOn: "बड़ा फ़ॉन्ट चालू",
        largeTextOff: "सामान्य दृश्य",
        langToggle: "English",
        // 4 Primary Action Cards
        cardWalkin: "Visitor Entry",
        cardWalkinSub: "नया विज़िटर या डिलीवरी दर्ज करें",
        cardScanQR: "QR पास स्कैन",
        cardScanQRSub: "कैमरा से गेट पास स्कैन करें",
        cardVehicle: "वाहन जांच",
        cardVehicleSub: "गाड़ी नंबर और फ्लैट खोजें",
        cardStaff: "दैनिक स्टाफ",
        cardStaffSub: "हाजिरी और अंदर/बाहर स्थिति",
        // Scanner & Code Verification
        passCodeTitle: "पास कोड सत्यापन",
        manualVerification: "कोड या फोन नंबर",
        codePlaceholder: "पास कोड दर्ज करें (उदा. A3F9B2C7 या DH-XXXX)",
        codeTip: "💡 टिप: 8-अंकों का पास कोड, वॉक-इन कोड, स्टाफ पास (DH-XXXX) या मोबाइल नंबर दर्ज करें।",
        verifyBtn: "सत्यापित करें",
        // Verified Pass Card
        verifiedPass: "सत्यापित पास",
        walkinGuest: "Visitor Entry",
        dailyStaff: "दैनिक स्टाफ",
        clearNext: "हटाएं / अगला",
        call: "कॉल",
        addPhoto: "फोटो जोड़ें",
        uploading: "अपलोड हो रहा है…",
        markEntry: "प्रवेश दर्ज करें",
        markExit: "प्रस्थान दर्ज करें",
        status: "स्थिति",
        passId: "पास आईडी",
        // Recent Activity
        todayAtGate: "आज गेट की हलचल",
        recentActivity: "हाल की गतिविधि",
        refresh: "रिफ्रेश",
        syncing: "लोड हो रहा है…",
        all: "सभी",
        inside: "अंदर",
        visitors: "Visitors",
        staff: "स्टाफ",
        noActivity: "इस दृश्य के लिए कोई गतिविधि दर्ज नहीं है।",
        noActivitySub: "सत्यापित पास, Visitor Entry और स्टाफ की हाजिरी यहाँ दिखेगी।",
        entryBtn: "प्रवेश",
        exitBtn: "बाहर",
        // Status Labels
        stEntered: "अंदर",
        stExited: "बाहर",
        stApproved: "स्वीकृत",
        stPending: "प्रतीक्षारत",
        stRejected: "अस्वीकृत",
        // Walkin Modal
        gateEntryDesk: "गेट एंट्री डेस्क",
        newWalkin: "New Visitor Entry (Walk-in)",
        visitorPhotoReq: "Visitor फोटो (अनिवार्य)",
        openCamera: "कैमरा खोलें / फोटो खींचें",
        clickToTake: "कैमरे से Visitor की फोटो लेने के लिए क्लिक करें",
        changePhoto: "फोटो बदलने के लिए टैप करें",
        visitorFullName: "Visitor का पूरा नाम *",
        spokenAs: "नाम बोलकर भी लिख सकते हैं",
        refreshMic: "माइक रिफ्रेश",
        namePlaceholder: "नाम लिखें या बोलें (उदा. रमेश शर्मा)",
        listeningName: "सुन रहे हैं... पूरा नाम बोलें",
        towerWing: "टावर / विंग *",
        selectTower: "टावर चुनें",
        flatNumber: "फ्लैट नंबर *",
        flatPlaceholder: "उदा. 402",
        purposeOfVisit: "आने का कारण",
        visitorVehicle: "Visitor वाहन (कार / बाइक / पैदल)",
        onFoot: "पैदल",
        car: "कार",
        bike: "बाइक",
        plateNumber: "गाड़ी लाइसेंस प्लेट नंबर",
        platePlaceholderBike: "उदा. MH 12 CD 5678",
        platePlaceholderCar: "उदा. MH 12 AB 1234",
        visitorPhone: "Visitor मोबाइल नंबर (वैकल्पिक)",
        phonePlaceholder: "10 अंकों का नंबर बोलें या लिखें",
        listeningPhone: "सुन रहे हैं... 10 अंक बोलें",
        digitsCount: "अंक",
        micHelpTitle: "माइक सहायता:",
        micHelpBody: "🎤 बोलने के लिए टैप करें। यदि आवाज़ दर्ज न हो तो माइक रिफ्रेश दबाएं।",
        sendForApproval: "निवासी को अनुमोदन के लिए भेजें",
        sendingApproval: "अपलोड व भेजा जा रहा है…",
        // Walkin Result
        walkinSent: "Visitor Entry भेजी गई",
        destFlat: "गंतव्य फ्लैट",
        resident: "निवासी",
        currentStatus: "वर्तमान स्थिति:",
        residentNotified: "निवासी को सूचना भेज दी गई है। आप व्हाट्सएप से भी अनुमोदन लिंक भेज सकते हैं:",
        doneClose: "संपन्न / बंद करें",
        sendToResidentWA: "निवासी को भेजें",
    }
};

const PURPOSE_OPTIONS = [
    { value: "Visitor", en: "Guest / Personal Visitor", hi: "मेहमान / व्यक्तिगत मुलाक़ात" },
    { value: "Delivery", en: "Courier / Delivery (Amazon, Swiggy, etc.)", hi: "डिलीवरी / कूरियर (Amazon, Swiggy आदि)" },
    { value: "Cab / Driver", en: "Cab / Driver (Uber, Ola)", hi: "कैब / ड्राइवर (Uber, Ola)" },
    { value: "Maid / Help", en: "Maid / House Help", hi: "बाई / घरेलू सहायिका" },
    { value: "Maintenance", en: "Maintenance / Technician", hi: "मरम्मत / तकनीशियन" },
    { value: "Other", en: "Other Purpose", hi: "अन्य कार्य" },
];

export function getActivityTimestamp(item) {
    if (!item) return 0;
    const raw = item.exited_at || item.entered_at || item.last_exit_at || item.last_entry_at || item.decided_at || item.updated_at || item.created_at;
    if (!raw) return 0;
    const t = new Date(raw).getTime();
    return isNaN(t) ? 0 : t;
}

export function formatActivityTime(item, lang = "en") {
    if (!item) return "";
    const isHi = lang === "hi";
    let label = isHi ? "दर्ज" : "Created";
    let timeVal = item.created_at;

    if (item.status === "exited" && item.exited_at) {
        label = isHi ? "प्रस्थान" : "Exited";
        timeVal = item.exited_at;
    } else if (item.status === "entered" && (item.entered_at || item.last_entry_at)) {
        label = isHi ? "प्रवेश" : "Entered";
        timeVal = item.entered_at || item.last_entry_at;
    } else if (item.status === "approved" && item.decided_at) {
        label = isHi ? "स्वीकृत" : "Approved";
        timeVal = item.decided_at;
    } else if (item.decided_at) {
        label = item.status === "rejected" ? (isHi ? "अस्वीकृत" : "Rejected") : (isHi ? "अपडेट" : "Updated");
        timeVal = item.decided_at;
    }

    if (!timeVal) return "";
    try {
        const d = new Date(timeVal);
        if (isNaN(d.getTime())) return "";
        const now = Date.now();
        const diffMs = now - d.getTime();
        const diffMins = Math.floor(diffMs / (1000 * 60));

        let timeStr = "";
        if (diffMins < 1) {
            timeStr = isHi ? "अभी-अभी" : "just now";
        } else if (diffMins < 60) {
            timeStr = isHi ? `${diffMins} मि. पहले` : `${diffMins}m ago`;
        } else {
            timeStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
        }
        return `${label} · ${timeStr}`;
    } catch {
        return "";
    }
}

export default function Guard() {
    const navigate = useNavigate();
    const [code, setCode] = useState("");
    const [visitor, setVisitor] = useState(null);
    const [logs, setLogs] = useState([]);
    const [walkinOpen, setWalkinOpen] = useState(false);
    const [walkinResult, setWalkinResult] = useState(null);
    const walkinResultRef = useRef(null);
    walkinResultRef.current = walkinResult;

    const closeWalkinResult = () => {
        walkinResultRef.current = null;
        setWalkinResult(null);
    };

    const [walkins, setWalkins] = useState([]);
    const [lookupOpen, setLookupOpen] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [staffModalOpen, setStaffModalOpen] = useState(false);
    const [activePhotoVisitor, setActivePhotoVisitor] = useState(null);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [activityFilter, setActivityFilter] = useState("all"); // "all", "inside", "visitors", "staff"

    // Language state: "en" | "hi"
    const [lang, setLang] = useState(() => {
        try {
            return localStorage.getItem("guard_lang") || "en";
        } catch {
            return "en";
        }
    });

    const toggleLang = () => {
        setLang((prev) => {
            const next = prev === "en" ? "hi" : "en";
            try {
                localStorage.setItem("guard_lang", next);
            } catch {}
            toast.info(next === "hi" ? "हिन्दी भाषा सक्रिय 🇮🇳" : "English Language Active");
            return next;
        });
    };

    const t = I18N[lang] || I18N.en;
    const isHi = lang === "hi";

    // Senior Mode state
    const [seniorMode, setSeniorMode] = useState(() => {
        try {
            const saved = localStorage.getItem("guard_senior_mode");
            return saved !== "false"; // Default to ON for security guards
        } catch {
            return true;
        }
    });

    const toggleSeniorMode = () => {
        setSeniorMode((prev) => {
            const next = !prev;
            try {
                localStorage.setItem("guard_senior_mode", next ? "true" : "false");
            } catch {}
            toast.info(next ? (isHi ? "सीनियर मोड सक्रिय (बड़ा फ़ॉन्ट) 👓" : "Large Text Mode enabled (Senior Friendly) 👓") : (isHi ? "सामान्य दृश्य सक्रिय" : "Standard Text Mode enabled"));
            return next;
        });
    };

    const combineLogs = (visitorList = [], walkinList = [], dailyHelpList = []) => {
        const normVisitors = (visitorList || []).map((v) => ({
            ...v,
            _isWalkin: false,
            _isDailyHelp: false,
            activityTime: getActivityTimestamp(v),
        }));

        const normWalkins = (walkinList || []).map((w) => ({
            ...w,
            _isWalkin: true,
            _isDailyHelp: false,
            visitor_id: w.walkin_id,
            host_flat: `${w.tower}-${w.flat_no}`,
            code: (w.walkin_id || "").slice(-6).toUpperCase(),
            activityTime: getActivityTimestamp(w),
        }));

        const normDailyHelp = (dailyHelpList || []).map((dh) => {
            const isEntered = !dh.exited_at;
            const flatsStr = Array.isArray(dh.flats) && dh.flats.length ? dh.flats.join(", ") : "Society";
            return {
                ...dh,
                _isDailyHelp: true,
                _isWalkin: false,
                visitor_id: dh.log_id || dh.help_id,
                code: (dh.help_id || "").replace(/^dh_/, "DH-").toUpperCase(),
                purpose: `${dh.service_type || "Staff"} (Daily Staff)`,
                host_flat: flatsStr,
                status: isEntered ? "entered" : "exited",
                created_at: dh.entered_at || dh.date,
                has_photo: Boolean(dh.has_photo || dh.photo_path),
                photo_url: dh.photo_url || ((dh.has_photo || dh.photo_path) && dh.help_id ? `/api/daily-help/${dh.help_id}/photo` : null),
                activityTime: getActivityTimestamp({
                    exited_at: dh.exited_at,
                    entered_at: dh.entered_at,
                    created_at: dh.entered_at || dh.date,
                }),
            };
        });

        // Deduplicate daily staff so each staff member appears only once with their latest activity
        const latestStaffMap = new Map();
        for (const dh of normDailyHelp) {
            const key = dh.help_id || dh.name;
            if (!key) continue;
            if (!latestStaffMap.has(key) || dh.activityTime > latestStaffMap.get(key).activityTime) {
                latestStaffMap.set(key, dh);
            }
        }
        const dedupedStaff = Array.from(latestStaffMap.values());

        const combined = [...normVisitors, ...normWalkins, ...dedupedStaff];
        // Sort strictly by the latest gate event so anyone who just entered/exited is on TOP
        combined.sort((a, b) => b.activityTime - a.activityTime);
        return combined;
    };

    const load = async (showManualFeedback = false) => {
        if (showManualFeedback) setIsRefreshing(true);
        try {
            const [r, w, dh] = await Promise.all([
                api.get("/visitors").catch(() => ({ data: [] })),
                api.get("/walkins").catch(() => ({ data: [] })),
                api.get("/daily-help/logs").catch(() => ({ data: [] })),
            ]);
            setWalkins(w.data || []);
            setLogs(combineLogs(r.data || [], w.data || [], dh.data || []));
            setLastUpdated(new Date());
            if (walkinResultRef.current) {
                const targetId = walkinResultRef.current.walkin_id;
                const upd = (w.data || []).find((x) => x.walkin_id === targetId);
                if (upd && walkinResultRef.current && walkinResultRef.current.walkin_id === targetId) {
                    setWalkinResult(upd);
                }
            }
            if (showManualFeedback) toast.success(isHi ? "गेट गतिविधि रिफ्रेश हो गई" : "Gate activity refreshed");
        } catch {
            if (showManualFeedback) toast.error(isHi ? "गेट गतिविधि रिफ्रेश नहीं हो सकी" : "Could not refresh gate activity");
        } finally {
            if (showManualFeedback) setIsRefreshing(false);
        }
    };

    useEffect(() => {
        load();
        const interval = setInterval(() => {
            if (typeof document !== "undefined" && document.visibilityState === "visible") {
                load();
            }
        }, 15000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const insideCount = useMemo(() => logs.filter((v) => v.status === "entered").length, [logs]);
    const visitorCount = useMemo(() => logs.filter((v) => !v._isDailyHelp).length, [logs]);
    const staffCount = useMemo(() => logs.filter((v) => v._isDailyHelp).length, [logs]);

    const filteredLogs = useMemo(() => {
        return logs.filter((v) => {
            if (activityFilter === "inside") return v.status === "entered";
            if (activityFilter === "visitors") return !v._isDailyHelp;
            if (activityFilter === "staff") return Boolean(v._isDailyHelp);
            return true;
        });
    }, [logs, activityFilter]);

    const verifyWithCode = async (c) => {
        const target = (c || code).trim();
        if (!target) {
            setScannerOpen(true);
            return;
        }
        try {
            const r = await api.post("/visitors/verify", { code: target });
            setVisitor(r.data);
            toast.success(isHi ? "पास सत्यापित हो गया" : "Visitor verified");
        } catch (err) {
            setVisitor(null);
            toast.error(err?.response?.data?.detail || (isHi ? "अमान्य पास कोड" : "Invalid pass code"));
        }
    };

    const verify = async (e) => {
        e?.preventDefault();
        if (!code.trim()) {
            setScannerOpen(true);
            return;
        }
        await verifyWithCode(code);
    };

    const action = async (kind) => {
        if (!visitor) return;
        const currentName = visitor.name || "Visitor";
        try {
            let endpoint;
            if (visitor._isDailyHelp) {
                endpoint = kind === "entry"
                    ? `/daily-help/${visitor.help_id || visitor.visitor_id}/check-in`
                    : `/daily-help/${visitor.help_id || visitor.visitor_id}/check-out`;
            } else if (visitor._isWalkin) {
                endpoint = `/walkins/${visitor.walkin_id || visitor.visitor_id}/${kind}`;
            } else {
                endpoint = `/visitors/${visitor.visitor_id}/${kind}`;
            }
            await api.post(endpoint);
            // Immediately clear the visitor card and photo so screen is ready for next person
            setVisitor(null);
            setCode("");
            await load();
            toast.success(
                isHi
                    ? `✓ ${currentName} का ${kind === "entry" ? "प्रवेश" : "प्रस्थान"} दर्ज। गेट अगले स्कैन के लिए तैयार।`
                    : `✓ ${kind === "entry" ? "Entry" : "Exit"} marked for ${currentName}. Gate cleared for next scan.`
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || (isHi ? "कार्य विफल रहा" : "Action failed"));
        }
    };

    const markItemAction = async (v, kind) => {
        try {
            let endpoint;
            if (v._isDailyHelp) {
                endpoint = kind === "entry"
                    ? `/daily-help/${v.help_id || v.visitor_id}/check-in`
                    : `/daily-help/${v.help_id || v.visitor_id}/check-out`;
            } else if (v._isWalkin) {
                endpoint = `/walkins/${v.walkin_id || v.visitor_id}/${kind}`;
            } else {
                endpoint = `/visitors/${v.visitor_id}/${kind}`;
            }
            await api.post(endpoint);
            toast.success(kind === "entry" ? (isHi ? "प्रवेश दर्ज किया गया" : "Entry marked") : (isHi ? "प्रस्थान दर्ज किया गया" : "Exit marked"));
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || (isHi ? "कार्य विफल रहा" : "Action failed"));
        }
    };

    const handleAttachPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !visitor) return;
        setUploadingPhoto(true);
        try {
            const fd = new FormData();
            fd.append("photo", file);
            let endpoint;
            if (visitor._isDailyHelp) {
                endpoint = `/daily-help/${visitor.help_id || visitor.visitor_id}/photo`;
            } else if (visitor._isWalkin) {
                endpoint = `/walkins/${visitor.walkin_id || visitor.visitor_id}/photo`;
            } else {
                endpoint = `/visitors/${visitor.visitor_id}/photo`;
            }
            await api.post(endpoint, fd);
            toast.success(isHi ? "फोटो सफलतापूर्वक जोड़ी गई" : "Visitor photo attached successfully");
            setVisitor((prev) => ({ ...prev, has_photo: true, photo_path: "attached" }));
            await load();
        } catch {
            toast.error(isHi ? "फोटो जोड़ने में त्रुटि" : "Failed to attach visitor photo");
        } finally {
            setUploadingPhoto(false);
        }
    };

    return (
        <div className={`w-full max-w-full min-w-0 ${seniorMode ? "guard-senior-active" : ""}`} data-testid="guard-page">
            {/* Header Banner: Security Badge, Bilingual Switch, Senior Switch & Refresh */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3.5 sm:mb-4 bg-white border border-nest-line rounded-2xl p-3 sm:p-4 shadow-xs">
                <div className="flex items-center gap-2.5 min-w-0">
                    <span className="relative flex h-3 w-3 shrink-0">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                    </span>
                    <div className="min-w-0">
                        <h1 className={`${seniorMode ? "text-xl sm:text-2xl font-black" : "text-lg sm:text-xl font-bold"} text-nest-ink tracking-tight flex items-center gap-2 truncate`}>
                            <span>{t.consoleTitle}</span>
                        </h1>
                        <p className={`${seniorMode ? "text-xs sm:text-sm font-semibold text-nest-ink2" : "text-[11px] text-nest-ink3"} mt-0.5 truncate`}>
                            {t.activeDuty}
                        </p>
                    </div>
                </div>

                {/* Right controls: Language Toggle & Senior Mode Toggle */}
                <div className="flex items-center gap-2 shrink-0">
                    {/* Bilingual Language Switcher */}
                    <button
                        type="button"
                        onClick={toggleLang}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-2xl border border-nest-line bg-nest-alt/60 hover:bg-white hover:border-nest-clay/30 hover:shadow-xs text-xs font-bold text-nest-ink transition cursor-pointer active:scale-95"
                        title="Switch Language (English / हिन्दी)"
                        data-testid="toggle-language-btn"
                    >
                        <Languages size={15} className="text-nest-clay shrink-0" />
                        <span>{t.langToggle}</span>
                    </button>

                    {/* Senior View Toggle */}
                    <button
                        type="button"
                        role="switch"
                        aria-checked={seniorMode}
                        onClick={toggleSeniorMode}
                        className="group inline-flex items-center gap-2.5 px-3 py-2 rounded-2xl border border-nest-line bg-nest-alt/40 hover:bg-white hover:border-nest-clay/30 hover:shadow-xs transition-all duration-200 cursor-pointer active:scale-98 select-none"
                        title={seniorMode ? "Disable Senior Large Text" : "Enable Senior Large Text"}
                        data-testid="toggle-senior-mode-btn"
                    >
                        <div className="flex items-center gap-1.5">
                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center font-bold text-xs transition-colors ${
                                seniorMode ? "bg-amber-500/15 text-amber-700" : "bg-neutral-200/80 text-neutral-600 group-hover:bg-neutral-300"
                            }`}>
                                <Type size={13} className="stroke-[2.5]" />
                            </div>
                            <span className="hidden sm:inline text-xs font-bold text-nest-ink leading-tight">
                                {t.seniorMode}
                            </span>
                        </div>

                        {/* iOS-Style Pill Switch */}
                        <div
                            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors duration-200 ease-in-out ${
                                seniorMode ? "bg-emerald-600" : "bg-neutral-300"
                            }`}
                            aria-hidden="true"
                        >
                            <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                                    seniorMode ? "translate-x-4" : "translate-x-0"
                                }`}
                            />
                        </div>
                    </button>
                </div>
            </div>

            {/* 4 Prominent Dashboard Action Cards (2x2 grid on mobile, 4-col on desktop) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3.5 mb-4 w-full max-w-full min-w-0">
                {/* 1. Walk-in Visitor */}
                <button
                    onClick={() => setWalkinOpen(true)}
                    className={`flex flex-col items-start justify-between bg-white border-2 border-nest-clay/30 hover:border-nest-clay hover:bg-amber-50/30 rounded-2xl p-3.5 sm:p-4 text-left shadow-xs transition group cursor-pointer active:scale-98 relative overflow-hidden min-w-0 ${
                        seniorMode ? "min-h-[110px] sm:min-h-[120px]" : "min-h-[96px]"
                    }`}
                    data-testid="log-walkin-btn"
                >
                    <div className="flex items-center justify-between w-full">
                        <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-2xl bg-nest-clay text-white grid place-items-center shadow-xs group-hover:scale-105 transition">
                            <UserPlus size={seniorMode ? 22 : 18} />
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-wider text-nest-clay bg-nest-clay/10 px-2 py-0.5 rounded-full">
                            {isHi ? "एंट्री" : "Guest"}
                        </span>
                    </div>
                    <div className="mt-2 w-full min-w-0">
                        <p className={`font-black text-nest-ink tracking-tight truncate ${
                            seniorMode ? "text-base sm:text-lg" : "text-sm sm:text-base"
                        }`}>
                            {t.cardWalkin}
                        </p>
                        <p className={`text-nest-ink3 truncate ${seniorMode ? "text-xs font-semibold" : "text-[11px]"}`}>
                            {t.cardWalkinSub}
                        </p>
                    </div>
                </button>

                {/* 2. Scan QR Pass */}
                <button
                    onClick={() => setScannerOpen(true)}
                    className={`flex flex-col items-start justify-between bg-nest-ink text-white rounded-2xl p-3.5 sm:p-4 text-left shadow-sm hover:bg-black transition group cursor-pointer active:scale-98 relative overflow-hidden min-w-0 ${
                        seniorMode ? "min-h-[110px] sm:min-h-[120px]" : "min-h-[96px]"
                    }`}
                    data-testid="quick-scan-qr-btn"
                >
                    <div className="flex items-center justify-between w-full">
                        <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-2xl bg-emerald-500 text-neutral-950 grid place-items-center shadow-xs group-hover:scale-105 transition">
                            <ScanLine size={seniorMode ? 22 : 18} />
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-wider text-emerald-300 bg-emerald-500/20 px-2 py-0.5 rounded-full border border-emerald-400/30">
                            {isHi ? "कैमरा" : "Scan"}
                        </span>
                    </div>
                    <div className="mt-2 w-full min-w-0">
                        <p className={`font-black text-white tracking-tight truncate ${
                            seniorMode ? "text-base sm:text-lg" : "text-sm sm:text-base"
                        }`}>
                            {t.cardScanQR}
                        </p>
                        <p className={`text-white/60 truncate ${seniorMode ? "text-xs font-medium" : "text-[11px]"}`}>
                            {t.cardScanQRSub}
                        </p>
                    </div>
                </button>

                {/* 3. Vehicle Check */}
                <button
                    onClick={() => setLookupOpen(true)}
                    className={`flex flex-col items-start justify-between bg-white border-2 border-amber-300/80 hover:border-amber-400 hover:bg-amber-50/40 rounded-2xl p-3.5 sm:p-4 text-left shadow-xs transition group cursor-pointer active:scale-98 relative overflow-hidden min-w-0 ${
                        seniorMode ? "min-h-[110px] sm:min-h-[120px]" : "min-h-[96px]"
                    }`}
                    data-testid="vehicle-lookup-btn"
                >
                    <div className="flex items-center justify-between w-full">
                        <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-2xl bg-amber-500 text-white grid place-items-center shadow-xs group-hover:scale-105 transition">
                            <Car size={seniorMode ? 22 : 18} />
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-wider text-amber-900 bg-amber-100 px-2 py-0.5 rounded-full border border-amber-300">
                            {isHi ? "नंबर प्लेट" : "Lookup"}
                        </span>
                    </div>
                    <div className="mt-2 w-full min-w-0">
                        <p className={`font-black text-nest-ink tracking-tight truncate ${
                            seniorMode ? "text-base sm:text-lg" : "text-sm sm:text-base"
                        }`}>
                            {t.cardVehicle}
                        </p>
                        <p className={`text-nest-ink3 truncate ${seniorMode ? "text-xs font-semibold" : "text-[11px]"}`}>
                            {t.cardVehicleSub}
                        </p>
                    </div>
                </button>

                {/* 4. Daily Staff */}
                <button
                    onClick={() => setStaffModalOpen(true)}
                    className={`flex flex-col items-start justify-between bg-emerald-50/60 border-2 border-emerald-300 hover:border-emerald-500 hover:bg-emerald-100/50 rounded-2xl p-3.5 sm:p-4 text-left shadow-xs transition group cursor-pointer active:scale-98 relative overflow-hidden min-w-0 ${
                        seniorMode ? "min-h-[110px] sm:min-h-[120px]" : "min-h-[96px]"
                    }`}
                    data-testid="guard-daily-help-btn"
                >
                    <div className="flex items-center justify-between w-full">
                        <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-2xl bg-emerald-600 text-white grid place-items-center shadow-xs group-hover:scale-105 transition">
                            <Users size={seniorMode ? 22 : 18} />
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-wider text-emerald-900 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-300">
                            {isHi ? "हाजिरी" : "1-Tap"}
                        </span>
                    </div>
                    <div className="mt-2 w-full min-w-0">
                        <p className={`font-black text-emerald-950 tracking-tight truncate ${
                            seniorMode ? "text-base sm:text-lg" : "text-sm sm:text-base"
                        }`}>
                            {t.cardStaff}
                        </p>
                        <p className={`text-emerald-800 truncate ${seniorMode ? "text-xs font-semibold" : "text-[11px]"}`}>
                            {t.cardStaffSub}
                        </p>
                    </div>
                </button>
            </div>

            {/* Main Console Grid: Left Column Gate Scanner & Pass Card, Right Column Recent Activity */}
            <div className="w-full max-w-full min-w-0 lg:grid lg:grid-cols-5 lg:gap-6 items-start">
                {/* Left Gate Scanner Section */}
                <section className="w-full lg:col-span-3 self-start lg:sticky lg:top-24 mb-4 lg:mb-0">
                    {!visitor ? (
                        <div className="bg-nest-ink text-white rounded-2xl sm:rounded-3xl p-4 sm:p-5 relative overflow-hidden shadow-xl border border-white/10 w-full max-w-full min-w-0" data-testid="guard-console-box">
                            {/* Header Badge */}
                            <div className="flex items-center justify-between mb-3 gap-2 min-w-0">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="relative flex h-2.5 w-2.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                                    </span>
                                    <p className={`${seniorMode ? "text-xs sm:text-sm font-extrabold tracking-[0.15em]" : "text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em]"} text-nest-sand truncate`}>
                                        {t.passCodeTitle}
                                    </p>
                                </div>
                                <span className={`${seniorMode ? "text-xs font-bold px-2.5 py-0.5" : "text-[10px] font-mono px-2 py-0.5"} text-emerald-300 bg-emerald-500/20 border border-emerald-400/30 rounded-full shrink-0`}>
                                    {t.manualVerification}
                                </span>
                            </div>

                            {/* Manual Code Input Form */}
                            <form onSubmit={verify} className="flex items-center gap-2.5 w-full min-w-0">
                                <div className="relative flex-1 min-w-0">
                                    <input
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                                        placeholder={t.codePlaceholder}
                                        className={`w-full min-w-0 bg-white/10 border-2 border-white/25 rounded-2xl px-4 font-mono font-bold tracking-widest uppercase outline-none focus:border-nest-clay focus:ring-2 focus:ring-nest-clay text-white placeholder:text-white/40 transition ${
                                            seniorMode ? "h-14 sm:h-16 text-base sm:text-lg" : "h-11 sm:h-12 text-xs sm:text-sm"
                                        }`}
                                        data-testid="guard-code-input"
                                    />
                                    {code && (
                                        <button
                                            type="button"
                                            onClick={() => setCode("")}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-1.5 cursor-pointer"
                                        >
                                            <X size={seniorMode ? 20 : 16} />
                                        </button>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    className={`bg-white hover:bg-nest-sand text-nest-ink rounded-2xl font-black transition active:scale-95 shrink-0 flex items-center justify-center gap-2 shadow-sm cursor-pointer ${
                                        seniorMode ? "h-14 sm:h-16 px-5 sm:px-6 text-sm sm:text-base" : "h-11 sm:h-12 px-4 text-xs sm:text-sm"
                                    }`}
                                    data-testid="guard-verify-btn"
                                >
                                    <CheckCircle2 size={seniorMode ? 20 : 16} className="text-emerald-600 shrink-0" />
                                    <span>{t.verifyBtn}</span>
                                </button>
                            </form>

                            {/* Helpful Hint Line */}
                            <p className={`mt-2.5 text-white/50 ${seniorMode ? "text-xs" : "text-[11px]"}`}>
                                {t.codeTip}
                            </p>
                        </div>
                    ) : (
                        <div className="bg-white text-nest-ink rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-xl border-2 border-emerald-500 relative animate-fade-in w-full max-w-full min-w-0" data-testid="guard-visitor-card">
                            {/* Header with Badges & Clear Button */}
                            <div className="flex items-center justify-between pb-3 border-b border-nest-line gap-2 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                    <span className={`inline-flex items-center gap-1.5 font-extrabold uppercase tracking-wider rounded-full bg-emerald-100 text-emerald-900 border border-emerald-300 ${
                                        seniorMode ? "text-xs sm:text-sm px-3 py-1" : "text-[10px] sm:text-xs px-2 py-0.5"
                                    }`}>
                                        <CheckCircle2 size={seniorMode ? 15 : 12} className="text-emerald-700 shrink-0" />
                                        {t.verifiedPass}
                                    </span>
                                    {visitor._isWalkin && (
                                        <span className={`font-extrabold uppercase tracking-wider rounded-full bg-nest-clay/15 text-nest-clay border border-nest-clay/30 ${
                                            seniorMode ? "text-xs sm:text-sm px-3 py-1" : "text-[10px] px-2 py-0.5"
                                        }`}>
                                            {t.walkinGuest}
                                        </span>
                                    )}
                                    {visitor._isDailyHelp && (
                                        <span className={`font-extrabold uppercase tracking-wider rounded-full bg-blue-100 text-blue-900 border border-blue-300 ${
                                            seniorMode ? "text-xs sm:text-sm px-3 py-1" : "text-[10px] px-2 py-0.5"
                                        }`}>
                                            {t.dailyStaff}
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { setVisitor(null); setCode(""); }}
                                    className={`rounded-xl bg-nest-alt hover:bg-nest-line/80 text-nest-ink hover:text-black transition cursor-pointer flex items-center gap-1.5 font-bold shrink-0 ${
                                        seniorMode ? "px-3.5 py-2 text-xs sm:text-sm" : "px-2.5 py-1 text-xs"
                                    }`}
                                    title="Clear / Scan next visitor"
                                    data-testid="guard-clear-visitor-btn"
                                >
                                    <X size={seniorMode ? 16 : 14} />
                                    <span>{t.clearNext}</span>
                                </button>
                            </div>

                            {/* Visitor Info Row */}
                            <div className="mt-3.5 flex items-start gap-3.5 min-w-0">
                                {/* Visitor Photo Avatar */}
                                {Boolean(visitor._isWalkin || visitor.has_photo || visitor.photo_path || visitor.photo_url) ? (
                                    <button
                                        type="button"
                                        onClick={() => setActivePhotoVisitor(visitor)}
                                        className={`relative group rounded-2xl overflow-hidden border-2 border-emerald-500/50 shrink-0 hover:ring-2 hover:ring-nest-clay transition cursor-pointer shadow-xs bg-nest-alt ${
                                            seniorMode ? "h-18 w-18 sm:h-22 sm:w-22" : "h-14 w-14 sm:h-18 sm:w-18"
                                        }`}
                                        title="Click to zoom photo"
                                        data-testid="guard-inspect-photo-btn"
                                    >
                                        <img
                                            src={getVisitorPhotoSrc(visitor)}
                                            alt=""
                                            onError={(e) => {
                                                e.currentTarget.style.display = "none";
                                                if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = "grid";
                                            }}
                                            className="h-full w-full object-cover group-hover:scale-105 transition"
                                        />
                                        <div style={{ display: "none" }} className="h-full w-full bg-nest-alt place-items-center text-nest-ink3">
                                            <User size={seniorMode ? 26 : 20} />
                                        </div>
                                    </button>
                                ) : (
                                    <div className={`rounded-2xl border-2 border-dashed border-nest-line bg-nest-alt/70 shrink-0 grid place-items-center text-nest-ink3 ${
                                        seniorMode ? "h-18 w-18 sm:h-22 sm:w-22" : "h-14 w-14 sm:h-18 sm:w-18"
                                    }`}>
                                        <User size={seniorMode ? 28 : 22} />
                                    </div>
                                )}

                                {/* Details */}
                                <div className="min-w-0 flex-1">
                                    <h2 className={`${seniorMode ? "text-xl sm:text-2xl" : "text-lg sm:text-xl"} font-black tracking-tight truncate leading-tight text-nest-ink`}>
                                        {visitor.name}
                                    </h2>
                                    <p className={`${seniorMode ? "text-sm sm:text-base font-semibold" : "text-xs"} text-nest-ink2 mt-1 truncate`}>
                                        {visitor.purpose} · <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded-md font-bold text-nest-ink">{visitor.host_flat || visitor.host_name}</span>
                                    </p>

                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                        {visitor.phone && (
                                            <a
                                                href={`tel:${String(visitor.phone).replace(/[^0-9+]/g, "")}`}
                                                className={`inline-flex items-center gap-1.5 rounded-lg bg-amber-50 border border-amber-200 text-nest-clay hover:underline font-bold ${
                                                    seniorMode ? "text-xs sm:text-sm px-2.5 py-1" : "text-[11px] px-1.5 py-0.5"
                                                }`}
                                                title={`Call ${visitor.phone}`}
                                            >
                                                <Phone size={seniorMode ? 14 : 11} />
                                                <span>{visitor.phone}</span>
                                            </a>
                                        )}
                                        {visitor.vehicle_no && (
                                            <span className={`inline-flex items-center gap-1.5 font-mono font-extrabold rounded-lg bg-nest-alt border border-nest-line text-nest-ink ${
                                                seniorMode ? "text-xs sm:text-sm px-2.5 py-1" : "text-[10px] px-1.5 py-0.5"
                                            }`} title="Vehicle">
                                                {visitor.vehicle_type === "bike" ? <Bike size={seniorMode ? 14 : 11} className="text-nest-clay" /> : <Car size={seniorMode ? 14 : 11} className="text-nest-clay" />}
                                                <span>{visitor.vehicle_no}</span>
                                            </span>
                                        )}
                                        {!Boolean(visitor._isWalkin || visitor.has_photo || visitor.photo_path || visitor.photo_url) && (
                                            <label className={`cursor-pointer font-bold rounded-lg bg-nest-alt border border-dashed border-nest-line hover:border-nest-clay text-nest-ink inline-flex items-center gap-1.5 ${
                                                seniorMode ? "text-xs sm:text-sm px-2.5 py-1" : "text-[10px] px-1.5 py-0.5"
                                            }`}>
                                                <Camera size={seniorMode ? 14 : 10} className="text-nest-clay" />
                                                <span>{uploadingPhoto ? t.uploading : t.addPhoto}</span>
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    capture="environment"
                                                    disabled={uploadingPhoto}
                                                    onChange={handleAttachPhoto}
                                                    className="hidden"
                                                    data-testid="guard-attach-photo-input"
                                                />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons: Giant, High-Contrast */}
                            <div className="mt-4 grid grid-cols-2 gap-3 sm:gap-4 w-full">
                                <button
                                    onClick={() => action("entry")}
                                    className={`w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black active:scale-95 transition flex items-center justify-center gap-2 cursor-pointer shadow-lg tracking-wide ${
                                        seniorMode ? "h-15 sm:h-17 text-base sm:text-lg" : "py-3 sm:py-3.5 text-xs sm:text-sm"
                                    }`}
                                    data-testid="guard-entry-btn"
                                >
                                    <LogIn size={seniorMode ? 22 : 16} />
                                    <span>{t.markEntry}</span>
                                </button>
                                <button
                                    onClick={() => action("exit")}
                                    className={`w-full bg-neutral-900 hover:bg-black text-white rounded-2xl font-black active:scale-95 transition flex items-center justify-center gap-2 cursor-pointer shadow-lg tracking-wide ${
                                        seniorMode ? "h-15 sm:h-17 text-base sm:text-lg" : "py-3 sm:py-3.5 text-xs sm:text-sm"
                                    }`}
                                    data-testid="guard-exit-btn"
                                >
                                    <LogOutIcon size={seniorMode ? 22 : 16} />
                                    <span>{t.markExit}</span>
                                </button>
                            </div>

                            {/* Footer Status Line */}
                            <div className={`mt-3 pt-2.5 border-t border-nest-line flex items-center justify-between ${seniorMode ? "text-xs sm:text-sm" : "text-[10px]"} text-nest-ink2`}>
                                <span>{t.status}: <strong className="capitalize text-nest-ink font-bold">{visitor.status}</strong></span>
                                <span>{t.passId}: <strong className="font-mono text-nest-ink font-bold">{visitor.code || visitor.pass_code || visitor.walkin_id}</strong></span>
                            </div>
                        </div>
                    )}
                </section>

                {/* Right Gate Activity Section */}
                <aside className="lg:col-span-2 nest-card p-3.5 sm:p-6 flex flex-col mt-3.5 lg:mt-0 w-full max-w-full min-w-0" data-testid="guard-recent-activity-panel">
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="nest-stat-label">{t.todayAtGate}</p>
                            <h3 className="font-display text-2xl mt-0.5 tracking-tight flex items-center gap-2">
                                <span>{t.recentActivity}</span>
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-nest-alt text-nest-ink font-sans">
                                    {logs.length}
                                </span>
                            </h3>
                        </div>
                        <button
                            type="button"
                            onClick={() => load(true)}
                            disabled={isRefreshing}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-nest-ink3 hover:text-nest-ink p-2 rounded-xl hover:bg-nest-alt transition cursor-pointer active:scale-95"
                            title="Refresh activity list"
                            data-testid="refresh-gate-activity-btn"
                        >
                            <RefreshCw size={14} className={isRefreshing ? "animate-spin text-nest-clay" : ""} />
                            <span className="hidden sm:inline">{isRefreshing ? t.syncing : t.refresh}</span>
                        </button>
                    </div>

                    {/* Filter Pills */}
                    <div className="flex items-center gap-2 mt-3.5 overflow-x-auto no-scrollbar pb-1.5">
                        <button
                            type="button"
                            onClick={() => setActivityFilter("all")}
                            className={`rounded-xl font-bold transition cursor-pointer whitespace-nowrap ${
                                seniorMode ? "h-11 px-4 text-xs sm:text-sm" : "px-3 py-1 text-xs"
                            } ${
                                activityFilter === "all" ? "bg-nest-ink text-white shadow-xs" : "bg-nest-alt hover:bg-nest-line text-nest-ink"
                            }`}
                        >
                            {t.all} ({logs.length})
                        </button>
                        <button
                            type="button"
                            onClick={() => setActivityFilter("inside")}
                            className={`rounded-xl font-bold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                                seniorMode ? "h-11 px-4 text-xs sm:text-sm" : "px-3 py-1 text-xs"
                            } ${
                                activityFilter === "inside" ? "bg-emerald-600 text-white shadow-xs" : "bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200"
                            }`}
                        >
                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                            {t.inside} ({insideCount})
                        </button>
                        <button
                            type="button"
                            onClick={() => setActivityFilter("visitors")}
                            className={`rounded-xl font-bold transition cursor-pointer whitespace-nowrap ${
                                seniorMode ? "h-11 px-4 text-xs sm:text-sm" : "px-3 py-1 text-xs"
                            } ${
                                activityFilter === "visitors" ? "bg-nest-ink text-white shadow-xs" : "bg-nest-alt hover:bg-nest-line text-nest-ink"
                            }`}
                        >
                            {t.visitors} ({visitorCount})
                        </button>
                        <button
                            type="button"
                            onClick={() => setActivityFilter("staff")}
                            className={`rounded-xl font-bold transition cursor-pointer whitespace-nowrap ${
                                seniorMode ? "h-11 px-4 text-xs sm:text-sm" : "px-3 py-1 text-xs"
                            } ${
                                activityFilter === "staff" ? "bg-nest-ink text-white shadow-xs" : "bg-nest-alt hover:bg-nest-line text-nest-ink"
                            }`}
                        >
                            {t.staff} ({staffCount})
                        </button>
                    </div>

                    {/* Feed Rows */}
                    <div className="mt-4 divide-y divide-nest-line overflow-y-auto max-h-[640px] pr-1">
                        {filteredLogs.length === 0 ? (
                            <div className="py-10 text-center text-nest-ink2 space-y-1.5" data-testid="guard-empty">
                                <p className={`${seniorMode ? "text-base font-bold text-nest-ink" : "text-sm font-medium"}`}>{t.noActivity}</p>
                                <p className={`${seniorMode ? "text-xs sm:text-sm" : "text-xs"} text-nest-ink3`}>{t.noActivitySub}</p>
                            </div>
                        ) : (
                            filteredLogs.slice(0, 35).map((v) => {
                                const hasPhoto = Boolean(v._isWalkin || v.has_photo || v.photo_path || v.photo_url);
                                const activityText = formatActivityTime(v, lang);
                                const statusLabel = isHi
                                    ? (v.status === "entered" ? t.stEntered : v.status === "exited" ? t.stExited : v.status === "approved" ? t.stApproved : v.status === "rejected" ? t.stRejected : t.stPending)
                                    : v.status;

                                return (
                                    <div
                                        key={v.log_id || v.visitor_id || v.walkin_id || v.code}
                                        className={`flex items-center justify-between gap-3 transition hover:bg-nest-alt/50 px-2 rounded-2xl w-full min-w-0 ${
                                            seniorMode ? "py-3.5" : "py-2.5"
                                        }`}
                                        data-testid={`guard-log-${v.code || v.walkin_id || v.help_id || v.log_id}`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0 pr-1 flex-1">
                                            {hasPhoto ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setActivePhotoVisitor(v)}
                                                    className={`rounded-2xl overflow-hidden border-2 border-nest-line shrink-0 bg-nest-alt hover:ring-2 hover:ring-nest-clay transition cursor-pointer relative group ${
                                                        seniorMode ? "h-12 w-12 sm:h-14 sm:w-14" : "h-9 w-9 sm:h-10 sm:w-10"
                                                    }`}
                                                    title="Check photo"
                                                    data-testid={`guard-log-photo-${v.code || v.walkin_id || v.help_id}`}
                                                >
                                                    <img
                                                        src={getVisitorPhotoSrc(v)}
                                                        alt=""
                                                        loading="lazy"
                                                        decoding="async"
                                                        onError={(e) => {
                                                            e.currentTarget.style.display = "none";
                                                            if (e.currentTarget.nextElementSibling) e.currentTarget.nextElementSibling.style.display = "grid";
                                                        }}
                                                        className="h-full w-full object-cover"
                                                    />
                                                    <div style={{ display: "none" }} className="h-full w-full bg-nest-alt/80 place-items-center text-nest-ink3">
                                                        <User size={seniorMode ? 20 : 15} />
                                                    </div>
                                                </button>
                                            ) : (
                                                <div className={`rounded-2xl border-2 border-nest-line bg-nest-alt/60 shrink-0 grid place-items-center text-nest-ink3 ${
                                                    seniorMode ? "h-12 w-12 sm:h-14 sm:w-14" : "h-9 w-9 sm:h-10 sm:w-10"
                                                }`}>
                                                    <User size={seniorMode ? 22 : 15} />
                                                </div>
                                            )}

                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <p className={`font-black truncate text-nest-ink leading-tight ${
                                                        seniorMode ? "text-sm sm:text-base" : "text-xs sm:text-sm"
                                                    }`}>{v.name}</p>
                                                    {v._isDailyHelp && (
                                                        <span className={`font-bold uppercase tracking-wider rounded-md bg-emerald-100 text-emerald-900 border border-emerald-300 ${
                                                            seniorMode ? "text-[11px] sm:text-xs px-2 py-0.5" : "text-[9px] px-1.5 py-0.2"
                                                        }`}>
                                                            {v.service_type || "Staff"}
                                                        </span>
                                                    )}
                                                    {v._isWalkin && (
                                                        <span className={`font-bold uppercase tracking-wider rounded-md bg-nest-clay/15 text-nest-clay border border-nest-clay/20 ${
                                                            seniorMode ? "text-[11px] sm:text-xs px-2 py-0.5" : "text-[9px] px-1.5 py-0.2"
                                                        }`}>
                                                            Walk-in
                                                        </span>
                                                    )}
                                                    {v.vehicle_no && (
                                                        <span className={`inline-flex items-center gap-1 font-mono font-bold bg-nest-alt rounded-md border border-nest-line text-nest-ink ${
                                                            seniorMode ? "text-[11px] sm:text-xs px-2 py-0.5" : "text-[9px] px-1 py-0.2"
                                                        }`} title="Vehicle">
                                                            {v.vehicle_type === "bike" ? <Bike size={seniorMode ? 13 : 10} className="text-nest-clay" /> : <Car size={seniorMode ? 13 : 10} className="text-nest-clay" />}
                                                            <span>{v.vehicle_no}</span>
                                                        </span>
                                                    )}
                                                </div>

                                                <p className={`truncate mt-1 ${seniorMode ? "text-xs sm:text-sm font-medium text-nest-ink2" : "text-[11px] text-nest-ink3"}`}>
                                                    {v.purpose} · <span className="font-bold text-nest-ink bg-amber-50 border border-amber-200 px-1.5 py-0.2 rounded">{v.host_flat || v.host_name}</span>
                                                </p>

                                                {activityText && (
                                                    <p className={`font-semibold text-nest-clay mt-1 flex items-center gap-1.5 ${seniorMode ? "text-xs" : "text-[10px]"}`}>
                                                        <Clock size={seniorMode ? 13 : 10} />
                                                        <span>{activityText}</span>
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
                                            <span className={`font-black uppercase tracking-wider rounded-xl whitespace-nowrap shadow-2xs ${
                                                seniorMode ? "text-xs sm:text-sm px-3 py-1" : "text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5"
                                            } ${
                                                v.status === "entered" ? "bg-emerald-600 text-white" :
                                                v.status === "exited" ? "bg-nest-sand text-nest-ink border border-nest-line" :
                                                v.status === "approved" ? "bg-blue-600 text-white" :
                                                v.status === "rejected" ? "bg-nest-err text-white" :
                                                "bg-amber-500 text-white"
                                            }`}>{statusLabel}</span>

                                            {v.status === "approved" && (
                                                <button
                                                    type="button"
                                                    onClick={() => markItemAction(v, "entry")}
                                                    className={`bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase rounded-xl transition cursor-pointer shrink-0 shadow-sm flex items-center justify-center gap-1 ${
                                                        seniorMode ? "h-10 sm:h-11 px-4 sm:px-5 text-xs sm:text-sm" : "px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs"
                                                    }`}
                                                    title="Allow entry"
                                                >
                                                    <LogIn size={seniorMode ? 16 : 13} />
                                                    <span>{t.entryBtn}</span>
                                                </button>
                                            )}
                                            {v.status === "entered" && (
                                                <button
                                                    type="button"
                                                    onClick={() => markItemAction(v, "exit")}
                                                    className={`bg-neutral-900 hover:bg-black text-white font-black uppercase rounded-xl transition cursor-pointer shrink-0 shadow-sm flex items-center justify-center gap-1 ${
                                                        seniorMode ? "h-10 sm:h-11 px-4 sm:px-5 text-xs sm:text-sm" : "px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs"
                                                    }`}
                                                    title="Mark exit"
                                                >
                                                    <LogOutIcon size={seniorMode ? 16 : 13} />
                                                    <span>{t.exitBtn}</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </aside>
            </div>

            {/* Modals */}
            <WalkinModal
                open={walkinOpen}
                onClose={() => setWalkinOpen(false)}
                onCreated={(w) => {
                    walkinResultRef.current = w;
                    setWalkinResult(w);
                    load();
                }}
                seniorMode={seniorMode}
                lang={lang}
            />

            {walkinResult && (
                <WalkinResult
                    walkin={walkinResult}
                    onClose={closeWalkinResult}
                    seniorMode={seniorMode}
                    lang={lang}
                />
            )}

            <VehicleGateLookup
                open={lookupOpen}
                onClose={() => setLookupOpen(false)}
                seniorMode={seniorMode}
                lang={lang}
            />

            <DailyStaffGateModal
                open={staffModalOpen}
                onClose={() => {
                    setStaffModalOpen(false);
                    load();
                }}
                onUpdated={load}
                seniorMode={seniorMode}
                lang={lang}
            />

            <VisitorPhotoModal
                visitor={activePhotoVisitor}
                onClose={() => setActivePhotoVisitor(null)}
                seniorMode={seniorMode}
            />

            <QRScannerModal
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScan={(scannedCode) => {
                    setCode(scannedCode);
                    verifyWithCode(scannedCode);
                }}
                seniorMode={seniorMode}
                lang={lang}
            />
        </div>
    );
}

// Convert spoken string to Title Case (First Name and Last Name)
export function formatSpokenName(raw) {
    if (!raw) return "";
    let cleaned = raw
        .replace(/^(my name is|mera naam|visitor name is|name is|naam|this is|i am|shri|mr|mrs|miss)\s+/i, "")
        .replace(/(\s+hai|\s+here)$/i, "")
        .replace(/[.,/#!$%^&*;:{}=\-_`~()]/g, "")
        .trim();

    const words = cleaned.split(/\s+/).filter(Boolean);
    const capitalized = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    return capitalized.join(" ");
}

// Convert spoken string or words to clean 10-digit phone number
export function formatSpokenPhone(raw) {
    if (!raw) return "";
    let text = raw.toLowerCase().trim();

    const wordToDigit = {
        zero: "0", shunya: "0", sunya: "0", sifar: "0",
        one: "1", ek: "1",
        two: "2", do: "2",
        three: "3", teen: "3", tin: "3",
        four: "4", chaar: "4", char: "4",
        five: "5", paanch: "5", panch: "5",
        six: "6", chhe: "6", chhah: "6", che: "6",
        seven: "7", saat: "7", sat: "7",
        eight: "8", aath: "8", ath: "8",
        nine: "9", nau: "9", no: "9",
        ten: "10", das: "10",
        eleven: "11", gyarah: "11",
        twelve: "12", barah: "12",
        twenty: "20", bees: "20",
        thirty: "30", tees: "30",
        forty: "40", chaalis: "40",
        fifty: "50", pachaas: "50",
        sixty: "60", saath: "60",
        seventy: "70", sattar: "70",
        eighty: "80", assi: "80",
        ninety: "90", nabbe: "90"
    };

    text = text.replace(/double\s+([a-z0-9]+)/gi, "$1 $1");
    text = text.replace(/triple\s+([a-z0-9]+)/gi, "$1 $1 $1");
    text = text.replace(/[,.]/g, " ");

    const tokens = text.split(/[\s-]+/);
    let digitStr = "";
    for (const t of tokens) {
        if (/^\d+$/.test(t)) {
            digitStr += t;
        } else if (wordToDigit[t]) {
            digitStr += wordToDigit[t];
        } else {
            const digitsOnly = t.replace(/\D/g, "");
            if (digitsOnly) digitStr += digitsOnly;
        }
    }

    let cleanDigits = digitStr.replace(/\D/g, "");
    if (cleanDigits.length === 12 && cleanDigits.startsWith("91")) {
        cleanDigits = cleanDigits.slice(2);
    } else if (cleanDigits.length === 11 && cleanDigits.startsWith("0")) {
        cleanDigits = cleanDigits.slice(1);
    }

    return cleanDigits.slice(0, 10);
}

function WalkinModal({ open, onClose, onCreated, seniorMode = true, lang = "en" }) {
    const isHi = lang === "hi";
    const t = I18N[lang] || I18N.en;

    const [form, setForm] = useState({ name: "", purpose: "Visitor", tower: "", flat_no: "", phone: "" });
    const [vehicleType, setVehicleType] = useState("none"); // "none" | "car" | "bike"
    const [vehicleNo, setVehicleNo] = useState("");
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [listeningField, setListeningField] = useState(null); // "name" | "phone" | null
    const [voiceLang, setVoiceLang] = useState(isHi ? "hi-IN" : "en-IN");
    const [refreshingMic, setRefreshingMic] = useState(false);
    const recognitionRef = useRef(null);
    const sessionTimerRef = useRef(null);
    const speechSilenceTimerRef = useRef(null);
    const isStartingRef = useRef(false);

    useEffect(() => {
        setVoiceLang(isHi ? "hi-IN" : "en-IN");
    }, [isHi]);

    const stopListening = () => {
        if (sessionTimerRef.current) {
            clearTimeout(sessionTimerRef.current);
            sessionTimerRef.current = null;
        }
        if (speechSilenceTimerRef.current) {
            clearTimeout(speechSilenceTimerRef.current);
            speechSilenceTimerRef.current = null;
        }
        if (recognitionRef.current) {
            const rec = recognitionRef.current;
            recognitionRef.current = null;
            try {
                rec.onstart = null;
                rec.onresult = null;
                rec.onerror = null;
                rec.onend = null;
                rec.abort();
            } catch (e) {}
        }
        setListeningField(null);
        isStartingRef.current = false;
    };

    const resetMicSession = () => {
        setRefreshingMic(true);
        stopListening();

        if (typeof navigator !== "undefined" && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            navigator.mediaDevices.getUserMedia({ audio: true })
                .then((stream) => {
                    stream.getTracks().forEach((trk) => trk.stop());
                })
                .catch(() => {});
        }

        setTimeout(() => {
            setRefreshingMic(false);
            toast.success(isHi ? "माइक तैयार है! बोलने के लिए टैप करें।" : "Microphone refreshed & ready! Tap mic to speak.");
        }, 350);
    };

    useEffect(() => {
        return () => {
            stopListening();
        };
    }, []);

    const startListening = (fieldName) => {
        const SpeechRecognition = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
        if (!SpeechRecognition) {
            toast.error(isHi ? "इस ब्राउज़र में आवाज़ इनपुट समर्थित नहीं है। कृपया Google Chrome का उपयोग करें।" : "Voice recording is not supported in this browser. Please use Google Chrome or Edge.");
            return;
        }

        if (listeningField === fieldName) {
            stopListening();
            toast.info(isHi ? "आवाज़ रिकॉर्डिंग बंद हुई" : "Voice recording stopped");
            return;
        }

        if (isStartingRef.current) return;
        isStartingRef.current = true;
        stopListening();
        setListeningField(fieldName);

        try {
            const rec = new SpeechRecognition();
            rec.continuous = false;
            rec.interimResults = true;
            rec.maxAlternatives = 1;
            rec.lang = voiceLang === "hi-IN" ? "hi-IN" : (voiceLang || "en-IN");

            let sessionTranscript = "";

            rec.onstart = () => {
                isStartingRef.current = false;
                toast.info(
                    fieldName === "name"
                        ? (isHi ? "🎤 Visitor का पूरा नाम बोलें" : "🎤 Speak visitor's full name")
                        : (isHi ? "🎤 10 अंकों का मोबाइल नंबर बोलें" : "🎤 Speak 10-digit mobile number")
                );
            };

            rec.onresult = (event) => {
                let accumulated = "";
                for (let i = 0; i < event.results.length; i++) {
                    const res = event.results[i];
                    if (res && res[0] && res[0].transcript) {
                        accumulated += res[0].transcript + " ";
                    }
                }
                accumulated = accumulated.trim();
                if (!accumulated) return;
                sessionTranscript = accumulated;

                if (fieldName === "name") {
                    const formatted = formatSpokenName(accumulated) || accumulated;
                    setForm((prev) => ({ ...prev, name: formatted }));
                } else if (fieldName === "phone") {
                    const digits = formatSpokenPhone(accumulated);
                    if (digits) {
                        setForm((prev) => ({ ...prev, phone: digits }));
                    }
                }

                if (speechSilenceTimerRef.current) {
                    clearTimeout(speechSilenceTimerRef.current);
                }
                speechSilenceTimerRef.current = setTimeout(() => {
                    stopListening();
                    if (sessionTranscript) {
                        if (fieldName === "name") {
                            const formatted = formatSpokenName(sessionTranscript) || sessionTranscript;
                            setForm((prev) => ({ ...prev, name: formatted }));
                            toast.success(`✓ Name: ${formatted}`);
                        } else if (fieldName === "phone") {
                            const digits = formatSpokenPhone(sessionTranscript);
                            if (digits && digits.length === 10) {
                                setForm((prev) => ({ ...prev, phone: digits }));
                                toast.success(`✓ Mobile: ${digits}`);
                            } else if (digits) {
                                setForm((prev) => ({ ...prev, phone: digits }));
                            }
                        }
                    }
                }, 1200);
            };

            rec.onerror = (event) => {
                isStartingRef.current = false;
                if (recognitionRef.current === rec) {
                    recognitionRef.current = null;
                }
                setListeningField(null);
                if (sessionTimerRef.current) {
                    clearTimeout(sessionTimerRef.current);
                    sessionTimerRef.current = null;
                }
                if (speechSilenceTimerRef.current) {
                    clearTimeout(speechSilenceTimerRef.current);
                    speechSilenceTimerRef.current = null;
                }

                if (event.error === "no-speech") {
                    toast.info(isHi ? "आवाज़ नहीं सुनाई दी। कृपया माइक के पास बोलें।" : "No speech detected. Please speak clearly into the mic.");
                } else if (event.error === "not-allowed" || event.error === "service-not-allowed") {
                    toast.error(isHi ? "माइक की अनुमति नहीं है। ब्राउज़र सेटिंग्स में माइक चालू करें।" : "Microphone access blocked. Please enable Microphone in browser Settings.");
                } else if (event.error !== "aborted") {
                    toast.error(`Mic: ${event.error}`);
                }
            };

            rec.onend = () => {
                isStartingRef.current = false;
                if (recognitionRef.current === rec) {
                    recognitionRef.current = null;
                }
                setListeningField(null);
            };

            recognitionRef.current = rec;
            rec.start();

            sessionTimerRef.current = setTimeout(() => {
                if (recognitionRef.current === rec) {
                    stopListening();
                }
            }, 8000);
        } catch {
            isStartingRef.current = false;
            recognitionRef.current = null;
            setListeningField(null);
            toast.error(isHi ? "माइक शुरू नहीं हो सका" : "Unable to start microphone");
        }
    };

    if (!open) return null;

    const onFile = (e) => {
        const f = e.target.files?.[0];
        setFile(f || null);
        if (f) setPreview(URL.createObjectURL(f)); else setPreview(null);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!file) {
            toast.error(isHi ? "पहले Visitor की फोटो लें या अपलोड करें" : "Capture or upload a visitor photo first");
            return;
        }
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("photo", file);
            Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
            if (vehicleType !== "none" && vehicleNo.trim()) {
                fd.append("vehicle_type", vehicleType);
                fd.append("vehicle_no", vehicleNo.trim().toUpperCase());
            }
            const r = await api.post("/walkins", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(isHi ? "Visitor विवरण निवासी को अनुमोदन हेतु भेजा गया!" : "Walk-in sent to resident for approval!");
            onCreated(r.data);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || (isHi ? "Visitor Entry दर्ज करने में त्रुटि" : "Failed to create walk-in"));
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto animate-fade-in" onClick={onClose}>
            <div className={`bg-white rounded-3xl border border-nest-line w-full max-w-xl ${seniorMode ? "p-5 sm:p-7" : "p-6 lg:p-8"} max-h-[90vh] overflow-y-auto my-auto shadow-2xl animate-scale-up`} onClick={(e) => e.stopPropagation()} data-testid="walkin-modal">
                <div className="flex items-center justify-between pb-3 border-b border-nest-line">
                    <div>
                        <span className={`font-black uppercase tracking-wider rounded-md text-nest-clay ${seniorMode ? "text-xs" : "nest-stat-label"}`}>
                            {t.gateEntryDesk}
                        </span>
                        <h3 className={`font-bold tracking-tight text-nest-ink ${seniorMode ? "text-2xl sm:text-3xl mt-0.5" : "font-display text-2xl mt-1"}`}>
                            {t.newWalkin}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="h-11 w-11 grid place-items-center rounded-2xl bg-nest-alt/70 hover:bg-nest-alt text-nest-ink transition cursor-pointer"
                        data-testid="walkin-close"
                        aria-label="Close"
                    >
                        <X size={22} />
                    </button>
                </div>

                <form onSubmit={submit} className={`mt-4 ${seniorMode ? "space-y-4" : "grid gap-4"}`}>
                    {/* Visitor Photo Block */}
                    <div className="rounded-2xl border-2 border-dashed border-nest-line p-3 bg-nest-alt/20">
                        <label className="block cursor-pointer">
                            <span className={`block font-bold text-nest-ink mb-1.5 ${seniorMode ? "text-sm sm:text-base" : "nest-stat-label"}`}>
                                📷 1. {t.visitorPhotoReq}
                            </span>
                            {preview ? (
                                <div className="relative">
                                    <img src={preview} alt="preview" className="w-full h-56 object-cover rounded-xl border border-nest-line"/>
                                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs font-bold px-3 py-1.5 rounded-lg">
                                        {t.changePhoto}
                                    </div>
                                </div>
                            ) : (
                                <div className="w-full h-44 rounded-xl border-2 border-dashed border-nest-clay/40 bg-white grid place-items-center text-nest-ink hover:bg-amber-50/50 transition">
                                    <div className="text-center p-3">
                                        <div className="h-14 w-14 rounded-full bg-nest-clay/10 text-nest-clay mx-auto grid place-items-center mb-2">
                                            <Camera size={30} />
                                        </div>
                                        <p className={`font-black text-nest-ink ${seniorMode ? "text-base sm:text-lg" : "text-sm"}`}>
                                            {t.openCamera}
                                        </p>
                                        <p className="text-xs text-nest-ink3 mt-0.5">{t.clickToTake}</p>
                                    </div>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={onFile}
                                className="mt-2 block w-full text-xs text-nest-ink2 cursor-pointer"
                                data-testid="walkin-photo-input"
                                required
                            />
                        </label>
                    </div>

                    {/* Visitor Name with Speech-to-Text Mic */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label className={`block font-bold text-nest-ink ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                                {t.visitorFullName}
                            </label>
                            <div className="flex items-center gap-2">
                                <div className="inline-flex rounded-lg border border-nest-line bg-nest-alt/60 p-0.5 text-[10px] font-bold">
                                    <button
                                        type="button"
                                        onClick={() => setVoiceLang("en-IN")}
                                        className={`px-1.5 py-0.5 rounded cursor-pointer transition ${voiceLang === "en-IN" ? "bg-white text-nest-ink shadow-2xs" : "text-nest-ink3 hover:text-nest-ink"}`}
                                    >
                                        EN
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setVoiceLang("hi-IN")}
                                        className={`px-1.5 py-0.5 rounded cursor-pointer transition ${voiceLang === "hi-IN" ? "bg-white text-nest-ink shadow-2xs" : "text-nest-ink3 hover:text-nest-ink"}`}
                                    >
                                        HI हिन्दी
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={resetMicSession}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-[10px] font-bold text-amber-900 transition cursor-pointer shadow-2xs"
                                    title="Reset mic"
                                    data-testid="walkin-reset-mic-name"
                                >
                                    <RefreshCw size={11} className={refreshingMic ? "animate-spin text-amber-700" : "text-amber-800"} />
                                    <span>{t.refreshMic}</span>
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <input
                                    required
                                    placeholder={t.namePlaceholder}
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className={`w-full bg-nest-alt/40 border border-nest-line focus:border-nest-clay focus:bg-white rounded-xl outline-none font-medium text-nest-ink transition ${
                                        form.name ? "pr-10" : ""
                                    } ${
                                        seniorMode ? "h-13 sm:h-14 px-4 text-base sm:text-lg" : "py-2 px-3 text-sm"
                                    }`}
                                    data-testid="walkin-name"
                                />
                                {form.name && (
                                    <button
                                        type="button"
                                        onClick={() => setForm((prev) => ({ ...prev, name: "" }))}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg text-nest-ink3 hover:text-nest-ink hover:bg-nest-alt grid place-items-center cursor-pointer"
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => startListening("name")}
                                className={`shrink-0 rounded-2xl font-bold flex items-center justify-center transition cursor-pointer active:scale-95 ${
                                    seniorMode ? "h-13 w-13 sm:h-14 sm:w-14" : "h-10 w-10 rounded-xl"
                                } ${
                                    listeningField === "name"
                                        ? "bg-red-500 text-white animate-pulse shadow-md shadow-red-500/40 ring-4 ring-red-200"
                                        : "bg-amber-500/15 hover:bg-amber-500/25 text-amber-800 border-2 border-amber-300"
                                }`}
                                title="Tap to speak name"
                                data-testid="walkin-name-mic-btn"
                            >
                                {listeningField === "name" ? (
                                    <MicOff size={seniorMode ? 22 : 18} />
                                ) : (
                                    <Mic size={seniorMode ? 22 : 18} />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Destination Tower Dropdown & Flat */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={`block font-bold text-nest-ink mb-1 ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                                {t.towerWing}
                            </label>
                            <select
                                required
                                value={form.tower}
                                onChange={(e) => setForm({ ...form, tower: e.target.value })}
                                className={`w-full bg-nest-alt/40 border border-nest-line focus:border-nest-clay focus:bg-white rounded-xl outline-none font-bold text-nest-ink transition cursor-pointer ${
                                    seniorMode ? "h-13 sm:h-14 px-3 text-base sm:text-lg" : "py-2 px-3 text-sm"
                                }`}
                                data-testid="walkin-tower"
                            >
                                <option value="" disabled>{t.selectTower}</option>
                                <option value="A">Tower A</option>
                                <option value="B">Tower B</option>
                                <option value="C">Tower C</option>
                                <option value="D">Tower D</option>
                                <option value="E">Tower E</option>
                                <option value="F">Tower F</option>
                            </select>
                        </div>
                        <div>
                            <label className={`block font-bold text-nest-ink mb-1 ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                                {t.flatNumber}
                            </label>
                            <input
                                required
                                type="text"
                                inputMode="numeric"
                                placeholder={t.flatPlaceholder}
                                value={form.flat_no}
                                onChange={(e) => setForm({ ...form, flat_no: e.target.value.replace(/[^0-9A-Za-z-]/g, "") })}
                                className={`w-full bg-nest-alt/40 border border-nest-line focus:border-nest-clay focus:bg-white rounded-xl outline-none font-bold text-nest-ink transition ${
                                    seniorMode ? "h-13 sm:h-14 px-4 text-base sm:text-lg" : "py-2 px-3 text-sm"
                                }`}
                                data-testid="walkin-flat"
                            />
                        </div>
                    </div>

                    {/* Purpose of visit */}
                    <div>
                        <label className={`block font-bold text-nest-ink mb-1 ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                            {t.purposeOfVisit}
                        </label>
                        <select
                            value={form.purpose}
                            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                            className={`w-full bg-nest-alt/40 border border-nest-line focus:border-nest-clay focus:bg-white rounded-xl outline-none font-bold text-nest-ink transition ${
                                seniorMode ? "h-13 sm:h-14 px-4 text-base" : "py-2 px-3 text-sm"
                            }`}
                            data-testid="walkin-purpose"
                        >
                            {PURPOSE_OPTIONS.map((p) => (
                                <option key={p.value} value={p.value}>
                                    {isHi ? p.hi : p.en}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Vehicle Selection for Visitor */}
                    <div className="p-3.5 sm:p-4 rounded-2xl border border-nest-line bg-nest-alt/40 space-y-2">
                        <label className={`font-black uppercase tracking-wider text-nest-ink block ${seniorMode ? "text-xs sm:text-sm" : "text-[11px]"}`}>
                            {t.visitorVehicle}
                        </label>
                        <div className="grid grid-cols-3 gap-2">
                            <button
                                type="button"
                                onClick={() => { setVehicleType("none"); setVehicleNo(""); }}
                                className={`rounded-xl border font-bold flex items-center justify-center gap-1.5 transition cursor-pointer ${
                                    seniorMode ? "h-12 sm:h-13 text-sm sm:text-base" : "py-2 text-xs"
                                } ${
                                    vehicleType === "none"
                                        ? "bg-nest-clay text-white border-nest-clay shadow-md"
                                        : "bg-white border-nest-line text-nest-ink hover:bg-nest-alt"
                                }`}
                                data-testid="walkin-veh-none"
                            >
                                <User size={seniorMode ? 18 : 14} /> <span>{t.onFoot}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setVehicleType("car")}
                                className={`rounded-xl border font-bold flex items-center justify-center gap-1.5 transition cursor-pointer ${
                                    seniorMode ? "h-12 sm:h-13 text-sm sm:text-base" : "py-2 text-xs"
                                } ${
                                    vehicleType === "car"
                                        ? "bg-nest-clay text-white border-nest-clay shadow-md"
                                        : "bg-white border-nest-line text-nest-ink hover:bg-nest-alt"
                                }`}
                                data-testid="walkin-veh-car"
                            >
                                <Car size={seniorMode ? 18 : 14} /> <span>{t.car}</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setVehicleType("bike")}
                                className={`rounded-xl border font-bold flex items-center justify-center gap-1.5 transition cursor-pointer ${
                                    seniorMode ? "h-12 sm:h-13 text-sm sm:text-base" : "py-2 text-xs"
                                } ${
                                    vehicleType === "bike"
                                        ? "bg-nest-clay text-white border-nest-clay shadow-md"
                                        : "bg-white border-nest-line text-nest-ink hover:bg-nest-alt"
                                }`}
                                data-testid="walkin-veh-bike"
                            >
                                <Bike size={seniorMode ? 18 : 14} /> <span>{t.bike}</span>
                            </button>
                        </div>

                        {vehicleType !== "none" && (
                            <div className="pt-2">
                                <label className="block text-xs font-bold text-nest-ink2 mb-1">{t.plateNumber}</label>
                                <input
                                    type="text"
                                    placeholder={vehicleType === "bike" ? t.platePlaceholderBike : t.platePlaceholderCar}
                                    value={vehicleNo}
                                    onChange={(e) => setVehicleNo(e.target.value.toUpperCase())}
                                    className={`w-full bg-white border border-nest-line focus:border-nest-clay rounded-xl px-4 font-mono font-bold uppercase outline-none text-nest-ink transition ${
                                        seniorMode ? "h-13 text-base sm:text-lg tracking-wider" : "py-2 text-xs"
                                    }`}
                                    data-testid="walkin-vehicle-input"
                                />
                            </div>
                        )}
                    </div>

                    {/* Visitor Phone with Speech-to-Text */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <label className={`block font-bold text-nest-ink ${seniorMode ? "text-sm sm:text-base" : "text-xs"}`}>
                                {t.visitorPhone}
                            </label>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={resetMicSession}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-[10px] font-bold text-amber-900 transition cursor-pointer shadow-2xs"
                                    title="Reset mic"
                                    data-testid="walkin-reset-mic-phone"
                                >
                                    <RefreshCw size={11} className={refreshingMic ? "animate-spin text-amber-700" : "text-amber-800"} />
                                    <span>{t.refreshMic}</span>
                                </button>
                                {form.phone && (
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${
                                        form.phone.length === 10
                                            ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                            : "bg-amber-100 text-amber-800"
                                    }`}>
                                        {form.phone.length === 10 ? "✓ 10 Digits" : `${form.phone.length}/10 ${t.digitsCount}`}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    maxLength={10}
                                    placeholder={t.phonePlaceholder}
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                                    className={`w-full bg-nest-alt/40 border border-nest-line focus:border-nest-clay focus:bg-white rounded-xl outline-none font-mono font-bold text-nest-ink transition ${
                                        form.phone ? "pr-10" : ""
                                    } ${
                                        seniorMode ? "h-13 sm:h-14 px-4 text-base sm:text-lg tracking-wider" : "py-2 px-3 text-sm"
                                    }`}
                                    data-testid="walkin-phone"
                                />
                                {form.phone && (
                                    <button
                                        type="button"
                                        onClick={() => setForm((prev) => ({ ...prev, phone: "" }))}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg text-nest-ink3 hover:text-nest-ink hover:bg-nest-alt grid place-items-center cursor-pointer"
                                    >
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => startListening("phone")}
                                className={`shrink-0 rounded-2xl font-bold flex items-center justify-center transition cursor-pointer active:scale-95 ${
                                    seniorMode ? "h-13 w-13 sm:h-14 sm:w-14" : "h-10 w-10 rounded-xl"
                                } ${
                                    listeningField === "phone"
                                        ? "bg-red-500 text-white animate-pulse shadow-md shadow-red-500/40 ring-4 ring-red-200"
                                        : "bg-amber-500/15 hover:bg-amber-500/25 text-amber-800 border-2 border-amber-300"
                                }`}
                                title="Tap to speak 10-digit mobile"
                                data-testid="walkin-phone-mic-btn"
                            >
                                {listeningField === "phone" ? (
                                    <MicOff size={seniorMode ? 22 : 18} />
                                ) : (
                                    <Mic size={seniorMode ? 22 : 18} />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={saving}
                        className={`w-full bg-nest-clay hover:bg-nest-clayDark text-white font-black uppercase rounded-2xl transition cursor-pointer shadow-lg active:scale-98 flex items-center justify-center gap-2 ${
                            seniorMode ? "h-14 sm:h-16 text-base sm:text-lg mt-3" : "nest-btn-primary mt-2"
                        }`}
                        data-testid="walkin-submit"
                    >
                        {saving ? (
                            <span>{t.sendingApproval}</span>
                        ) : (
                            <>
                                <LogIn size={seniorMode ? 22 : 18} />
                                <span>{t.sendForApproval}</span>
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>,
        document.body
    );
}

function WalkinResult({ walkin, onClose, seniorMode = true, lang = "en" }) {
    const isHi = lang === "hi";
    const t = I18N[lang] || I18N.en;
    const link = `${window.location.origin}/walkin/${walkin.walkin_id}`;
    const statusColor = walkin.status === "approved" || walkin.status === "entered" ? "bg-emerald-600 text-white" : walkin.status === "rejected" ? "bg-nest-err text-white" : "bg-amber-500 text-white";
    const vehIcon = walkin.vehicle_type === "bike" ? "🏍️" : "🚗";
    const vehText = walkin.vehicle_no ? `\nVehicle: ${vehIcon} ${walkin.vehicle_no}` : "";

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-4 overflow-y-auto animate-fade-in" onClick={onClose}>
            <div className={`bg-white rounded-3xl border border-nest-line w-full max-w-lg ${seniorMode ? "p-6 sm:p-8" : "p-5 sm:p-7"} my-auto shadow-2xl animate-scale-up`} onClick={(e) => e.stopPropagation()} data-testid="walkin-result">
                <div className="flex items-center justify-between pb-3 border-b border-nest-line/50">
                    <div>
                        <span className="text-xs font-black uppercase tracking-wider text-nest-clay">{t.walkinSent}</span>
                        <h3 className={`font-black text-nest-ink tracking-tight mt-0.5 ${seniorMode ? "text-2xl sm:text-3xl" : "font-display text-xl sm:text-2xl"}`}>
                            {walkin.name}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-12 w-12 grid place-items-center rounded-2xl bg-nest-alt/70 hover:bg-nest-alt text-nest-ink transition cursor-pointer"
                        aria-label="Close"
                        data-testid="walkin-result-close"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="mt-4 space-y-3">
                    <div className="p-3.5 bg-amber-50/70 border border-amber-200/80 rounded-2xl">
                        <span className="text-xs font-bold uppercase text-amber-800 block">{t.destFlat}</span>
                        <p className={`font-black text-amber-950 mt-0.5 ${seniorMode ? "text-xl sm:text-2xl" : "text-base sm:text-lg"}`}>
                            Flat {walkin.tower}-{walkin.flat_no}
                        </p>
                        <p className={`text-amber-900 mt-0.5 ${seniorMode ? "text-sm sm:text-base font-semibold" : "text-xs"}`}>
                            {t.resident}: {walkin.resident_name || "Resident"}
                        </p>
                    </div>

                    {walkin.vehicle_no && (
                        <div className="flex items-center gap-2 p-3 bg-nest-alt rounded-xl border border-nest-line">
                            {walkin.vehicle_type === "bike" ? <Bike size={18} className="text-nest-clay" /> : <Car size={18} className="text-nest-clay" />}
                            <span className="font-mono font-black text-base text-nest-ink">{walkin.vehicle_no}</span>
                        </div>
                    )}

                    <div className="flex items-center gap-2 pt-1">
                        <span className="text-xs font-bold text-nest-ink2 uppercase">{t.currentStatus}</span>
                        <span className={`inline-block font-black uppercase tracking-wider rounded-xl shadow-2xs ${
                            seniorMode ? "text-xs sm:text-sm px-3.5 py-1.5" : "text-[10px] px-2.5 py-1"
                        } ${statusColor}`} data-testid="walkin-result-status">
                            {isHi ? (walkin.status === "approved" ? t.stApproved : walkin.status === "rejected" ? t.stRejected : t.stPending) : walkin.status}
                        </span>
                    </div>

                    <div className="pt-2">
                        <p className={`text-nest-ink2 ${seniorMode ? "text-xs sm:text-sm font-medium" : "text-xs"}`}>
                            {t.residentNotified}
                        </p>
                        <p className="font-mono text-xs break-all bg-nest-alt p-3 rounded-xl border border-nest-line/60 mt-1.5 text-nest-ink select-all">{link}</p>
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-nest-line flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className={`rounded-2xl border border-nest-line bg-white hover:bg-nest-alt text-nest-ink font-bold transition active:scale-95 cursor-pointer shadow-2xs ${
                            seniorMode ? "h-13 px-6 text-sm sm:text-base" : "px-4 py-2.5 text-xs"
                        }`}
                        data-testid="walkin-result-done-btn"
                    >
                        {t.doneClose}
                    </button>
                    <ShareWhatsApp
                        testid="walkin-share-wa"
                        phone={walkin.phone}
                        label={t.sendToResidentWA}
                        text={`🚪 *Visitor at gate for ${walkin.tower}-${walkin.flat_no}*\n\nName: ${walkin.name}\nPurpose: ${walkin.purpose}${vehText}\n\nApprove/Reject: ${link}\n\n— NeighborNest`}
                    />
                </div>
            </div>
        </div>,
        document.body
    );
}
